import { Fragment } from "react";
import { StyleSheet } from "react-native";

import { PaymentMethod, useContentResource } from "@ctv/core";
import {
  Button,
  Card,
  DocumentField,
  InfoBox,
  InputRHF,
  Text,
  Token,
  View,
} from "@ctv/shared-core/src";

import IcSystemArrowLeft from "@traveloka/icon-kit-web/react/IcSystemArrowLeft";
import IcSystemDownload from "@traveloka/icon-kit-web/react/IcSystemDownload";

import { RegistrationFormValue } from "../../types";
import { FieldRow } from "../FieldRow";
import RequestNDAModal from "../RequestNDAModal";
import CompanyDocumentItem from "./_components/CompanyDocumentItem";
import ReferenceCheck from "./_components/ReferenceCheck";
import useRegistrationDocument from "./hooks";

const REASON_MAX_LENGTH = 200;

type Props = {
  onClickBack: () => void;
  onSubmit: (value: RegistrationFormValue) => Promise<void>;
};

export default function RegistrationDocuments({
  onSubmit,
  onClickBack,
}: Props) {
  const {
    cr,
    registrationData,
    isFormSubmitting,
    isRequestNDAVisible,
    onRequestNDA,
    handleSubmit,
    handleUploadDocument,
    setIsRequestNDAVisible,
    handleOnClickSaveAndExit,
    handleDownloadAgreementForm,
  } = useRegistrationDocument();

  const documentCr = useContentResource().CorporateRegistration;

  const {
    documents,
    documentFormList,
    documentCAFormList,
    ndaRequested,
    paymentMethod,
  } = registrationData;

  return (
    <View style={styles.container} row align="start">
      <View style={styles.startContent}>
        {/* Header */}
        <Button
          text={cr.back}
          variant="text-primary"
          style={styles.alignSelfStart}
          startIcon={<IcSystemArrowLeft height={16} width={16} />}
          onPress={onClickBack}
        />
        <Text variant="title-1" style={styles.section}>
          {cr.documentSectionV3}
        </Text>
        <Text
          variant="ui-small"
          ink="black-secondary"
          style={styles.marginBottomL}
        >
          {cr.documentSubInfo}
        </Text>
        {ndaRequested === false && (
          <Fragment>
            <InfoBox
              style={styles.marginBottomM}
              title={cr.documentRequestNdaTitle}
              children={
                <Text ink="highlight" variant="ui-small">
                  {cr.documentRequestNdaContent}
                </Text>
              }
              variant="info"
              Action={
                <View row align="center" spacing="xxs">
                  <IcSystemDownload width={18} height={18} />
                  <Text
                    ink="highlight"
                    variant="ui-small"
                    style={styles.infoTextAction}
                    onPress={() => setIsRequestNDAVisible(true)}
                  >
                    {cr.documentRequestNdaButton}
                  </Text>
                </View>
              }
            />
            <RequestNDAModal
              isVisible={isRequestNDAVisible}
              onSubmit={onRequestNDA}
              onClose={() => setIsRequestNDAVisible(false)}
            />
          </Fragment>
        )}
        {/* Form Group Company Document */}
        {!!documentFormList.length && (
          <Card style={styles.formGroup}>
            {documentFormList.map((document, index) => (
              <CompanyDocumentItem
                index={index}
                document={document}
                handleUpload={handleUploadDocument}
                documentValue={documents || []}
                key={`company_document__${index}__${document.label}`}
              />
            ))}
            <FieldRow
              style={styles.fieldRow}
              label={cr.creditAssessmentNotesLabel}
            >
              <InputRHF
                name="documentNotes"
                multiline
                style={styles.flex1}
                inputStyle={styles.heightXL}
                maxLength={REASON_MAX_LENGTH}
                placeholder={cr.documentNotesPlaceholder}
              />
            </FieldRow>
          </Card>
        )}
        {/* Form Group Credit Assesment */}
        {!!documentCAFormList.length && (
          <Fragment>
            <Text variant="title-1" style={styles.section}>
              {cr.creditAssessmentSection}
            </Text>
            <Card style={styles.formGroup}>
              {documentCAFormList.map((document, index) => (
                <CompanyDocumentItem
                  document={document}
                  index={index}
                  documentArrayMaxItem={6}
                  handleUpload={handleUploadDocument}
                  key={`company_document_ca_${index}__${document.label}`}
                />
              ))}
              <FieldRow
                style={styles.fieldRow}
                label={cr.creditAssessmentNotesLabel}
              >
                <InputRHF
                  name="creditAssessmentNotes"
                  placeholder={cr.creditAssessmentNotesPlaceholder}
                  style={styles.flex1}
                  inputStyle={styles.heightXL}
                  multiline
                  maxLength={REASON_MAX_LENGTH}
                />
              </FieldRow>
            </Card>
          </Fragment>
        )}
        {paymentMethod === PaymentMethod.INVOICE && (
          <Fragment>
            {/* Form Group Invoice Payment */}
            <Text variant="title-1" style={styles.section}>
              {cr.referenceCheckSection}
            </Text>
            <Text ink="black-secondary" style={styles.marginBottomM}>
              {cr.referenceCheckSubInfo}
            </Text>
            <Card style={[styles.formGroup, styles.zIndex1]}>
              <ReferenceCheck />
            </Card>
            {/* Form Group Agreement Form Invoice */}
            <Text variant="title-1" style={styles.section}>
              {cr.signedRegistrationForm}
            </Text>
            <Card style={styles.formGroup}>
              <InfoBox
                style={styles.marginBottomM}
                title={cr.agreementFormInfoBoxTitle}
                children={cr.agreementFormInfoBoxContent}
                Action={
                  <Text
                    ink="highlight"
                    variant="ui-small-bold"
                    onPress={handleDownloadAgreementForm}
                  >
                    {cr.agreementFormInfoBoxActionText}
                  </Text>
                }
              />
              <FieldRow label={cr.signedRegistrationFormFieldLabel} required>
                <DocumentField
                  name="agreementDocument.AGREEMENT_FORM_INVOICE"
                  document={{
                    docType: "AGREEMENT_FORM_INVOICE",
                    extensions: [".pdf", ".png", ".jpeg"],
                    label: "",
                    mandatory: true,
                    maxSizeInMB: "20",
                    placeholder: cr.requestNdaUploadPlaceholder,
                    multiple: false,
                    subLabel: null,
                    tooltip: null,
                    checkbox: null,
                  }}
                  onChange={handleUploadDocument}
                  documentExtensionCr={documentCr.documentExtension}
                  documentFileSizeCr={documentCr.documentFileSize}
                  documentRequiredCr={documentCr.documentRequired}
                />
              </FieldRow>
            </Card>
          </Fragment>
        )}
        {/* Submit Button */}
        <View row justify="end" style={styles.actionWrapper}>
          <Button
            variant="text-primary"
            text={cr.saveAndExitButtonText}
            onPress={handleOnClickSaveAndExit}
          />
          <Button
            onPress={handleSubmit(onSubmit)}
            loading={isFormSubmitting}
            style={styles.marginLeftL}
            variant="main-cta"
            text={cr.continueButtonText}
          />
        </View>
      </View>
      {/* Right Content */}
      <Card style={styles.endContent}>
        <Text style={styles.endContentTitle} variant="ui-small">
          {cr.moreTimeInfo}
        </Text>
        <Text variant="ui-tiny" ink="black-secondary">
          {cr.moreTimeInfoBody}
        </Text>
      </Card>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 960,
    position: "relative",
    marginTop: Token.spacing.xxl,
    marginHorizontal: "auto",
  },
  startContent: {
    flex: 1,
    marginRight: Token.spacing.xxxl,
  },
  section: {
    marginTop: Token.spacing.xl,
    marginBottom: Token.spacing.m,
  },
  infoTextAction: {
    //@ts-expect-error
    cursor: "pointer",
    fontWeight: "500",
    marginTop: Token.spacing.xxs,
    marginLeft: Token.spacing.xxs,
  },
  formGroup: {
    padding: Token.spacing.l,
    overflow: "visible",
  },
  fieldRow: {
    marginTop: Token.spacing.l,
  },
  actionWrapper: {
    marginTop: Token.spacing.xl,
    marginBottom: Token.spacing.m,
  },
  endContent: {
    width: 266,
    padding: Token.spacing.m,
    // @ts-expect-error
    position: "sticky",
    top: 2 * Token.spacing.xl + Token.spacing.xxxl,
  },
  endContentTitle: {
    fontWeight: "600",
    marginBottom: Token.spacing.m,
  },

  // General styles
  marginLeftL: {
    marginLeft: Token.spacing.l,
  },
  marginBottomM: {
    marginBottom: Token.spacing.m,
  },
  marginBottomL: {
    marginBottom: Token.spacing.l,
  },
  marginTopS: {
    marginTop: Token.spacing.s,
  },
  alignSelfStart: {
    alignSelf: "flex-start",
  },
  flex1: {
    flex: 1,
  },
  heightXL: {
    height: 64,
  },
  zIndex1: {
    zIndex: 1,
  },
});
